import React from "react";
import { Row, Col } from "reactstrap";
import Breadcrumb from "../../../components/Common/Breadcrumb";
import NeoCard from "../../../components/Common/NeoCard";
import NeoButton from "../../../components/Common/NeoButton";
import NeoBadge from "../../../components/Common/NeoBadge";
import NeoTable from "../../../components/Common/NeoTable";
import usePlatformPortfolio from "./hooks/usePlatformPortfolio";
import PlatformClientDetail from "./components/PlatformClientDetail";
import PlatformTransferModal from "./components/PlatformTransferModal";
import PlatformClientHealthBadge from "./components/PlatformClientHealthBadge";
import { PORTFOLIO_VIEWS, BILLING_STATUS_LABELS } from "./constants";
import "./PlatformPortfolio.css";

const PlatformPortfolio = () => {
    const {
        view,
        items,
        teamUsers,
        filterCsmId,
        search,
        loading,
        error,
        isManager,
        selectedClient,
        transferModal,
        selectedIds,
        setSearch,
        changeView,
        applyFilterCsm,
        setSelectedClient,
        handleAssignCsm,
        handleTransferCsm,
        openTransfer,
        closeTransfer,
        toggleSelectRow,
        handleSelectAll,
        platformRole,
    } = usePlatformPortfolio();

    // Identifica se todos os itens listados estão selecionados
    const isAllSelected = items.length > 0 && items.every(item => selectedIds.includes(item.id));

    // Definição de colunas da NeoTable
    const columns = [
        {
            key: "select",
            label: (
                <div className="checkbox-cell-wrapper">
                    <input
                        type="checkbox"
                        className="checkbox-pointer"
                        checked={isAllSelected}
                        onChange={() => handleSelectAll(items.map(i => i.id), isAllSelected)}
                    />
                </div>
            ),
            width: "50px",
            align: "center",
            render: (row) => (
                <div className="checkbox-cell-wrapper">
                    <input
                        type="checkbox"
                        className="checkbox-pointer"
                        checked={selectedIds.includes(row.id)}
                        onChange={(e) => {
                            e.stopPropagation(); // Evita selecionar a linha da tabela para detalhes
                            toggleSelectRow(row.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )
        },
        {
            key: "name",
            label: "Academia",
            render: (row) => (
                <div>
                    <span className="fw-bold text-dark font-size-12 d-block">{row.name}</span>
                    <span className="text-muted font-size-10">{row.ownerName || "Sem Responsável"}</span>
                </div>
            )
        },
        {
            key: "billingStatus",
            label: "Faturamento",
            width: "120px",
            render: (row) => {
                const billing = BILLING_STATUS_LABELS[row.billingStatus] || { label: row.billingStatus || "—", color: "muted" };
                return <NeoBadge color={billing.color}>{billing.label}</NeoBadge>;
            }
        },
        {
            key: "csmName",
            label: "CS Responsável",
            width: "150px",
            render: (row) => (
                <span className="fw-semibold text-secondary font-size-12">
                    {row.csmName || <span className="text-danger">Não atribuído</span>}
                </span>
            )
        },
        {
            key: "onboardingProgress",
            label: "Onboarding",
            width: "140px",
            render: (row) => {
                const pct = row.onboardingProgress?.pct ?? 0;
                return (
                    <div className="d-flex align-items-center gap-2 w-100">
                        <div className="progress csm-progress-container flex-grow-1">
                            <div
                                className={`progress-bar ${pct === 100 ? "csm-progress-bar-success" : "csm-progress-bar-brand"}`}
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                        <span className="font-size-10 fw-bold">{pct}%</span>
                    </div>
                );
            }
        },
        {
            key: "health",
            label: "Saúde",
            width: "100px",
            align: "center",
            render: (row) => row.health?.score !== undefined ? (
                <PlatformClientHealthBadge
                    score={row.health.score}
                    level={row.health.level}
                    compact
                />
            ) : <span className="text-muted font-size-11">—</span>
        }
    ];

    // Adiciona ações na coluna apenas para gestores (como transferência individual rápida)
    if (isManager) {
        columns.push({
            key: "actions",
            label: "Ações",
            width: "80px",
            align: "center",
            render: (row) => (
                <NeoButton
                    variant="soft-secondary"
                    size="xs"
                    square
                    icon="mdi-swap-horizontal"
                    onClick={(e) => {
                        e.stopPropagation();
                        openTransfer(row);
                    }}
                    title="Transferir CS"
                />
            )
        });
    }

    // Resolvendo o cliente selecionado com dados atualizados da lista
    const activeClient = selectedClient ? items.find(c => c.id === selectedClient.id) || selectedClient : null;

    return (
        <div className="pb-5">
            <div className="mb-4">
                <Breadcrumb title="Carteira de Clientes" />
            </div>

            {error && (
                <div className="alert alert-danger mb-4 shadow-sm" role="alert">
                    <i className="mdi mdi-alert-circle-outline me-2" />
                    {error}
                </div>
            )}

            <Row className="g-4">
                {/* Coluna Esquerda: Listagem em NeoTable */}
                <Col lg={activeClient ? 8 : 12} className="transition-all">
                    <NeoCard
                        title={
                            isManager
                                ? "Clientes da Plataforma"
                                : platformRole === "comercial"
                                    ? "Minhas Vendas (Clientes Convertidos)"
                                    : "Minha Carteira de Clientes (Pós-Venda)"
                        }
                        headerIcon="mdi-briefcase-outline"
                        thinBorder
                        bodyClassName="p-0"
                        className="h-100"
                        headerActions={
                            selectedIds.length > 0 && isManager ? (
                                <NeoButton
                                    variant="primary"
                                    size="sm"
                                    onClick={() => {
                                        const selectedClients = items.filter(c => selectedIds.includes(c.id));
                                        openTransfer(null, selectedClients);
                                    }}
                                >
                                    <i className="mdi mdi-swap-horizontal me-1" />
                                    Transferir em Lote ({selectedIds.length})
                                </NeoButton>
                            ) : null
                        }
                    >
                        <div className="p-4 border-bottom">
                            {/* Tabs de Filtro de Visão (Apenas para Gestores) */}
                            {isManager && (
                                <div className="portfolio-view-tabs">
                                    <button
                                        className={`portfolio-view-tab ${view === PORTFOLIO_VIEWS.csm ? "active" : ""}`}
                                        onClick={() => changeView(PORTFOLIO_VIEWS.csm)}
                                    >
                                        Meu Pós-Venda (CS)
                                    </button>
                                    <button
                                        className={`portfolio-view-tab ${view === PORTFOLIO_VIEWS.sales ? "active" : ""}`}
                                        onClick={() => changeView(PORTFOLIO_VIEWS.sales)}
                                    >
                                        Minhas Vendas
                                    </button>
                                    <button
                                        className={`portfolio-view-tab ${view === PORTFOLIO_VIEWS.all ? "active" : ""}`}
                                        onClick={() => changeView(PORTFOLIO_VIEWS.all)}
                                    >
                                        Toda a Carteira
                                    </button>
                                </div>
                            )}

                            <Row className="g-3 align-items-center">
                                <Col md={view === PORTFOLIO_VIEWS.all && isManager ? 6 : 12}>
                                    {/* Barra de Busca */}
                                    <div className="portfolio-search-bar">
                                        <i className="mdi mdi-magnify search-icon" />
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Buscar cliente, CS ou vendedor..."
                                            value={search}
                                            onChange={e => setSearch(e.target.value)}
                                        />
                                    </div>
                                </Col>

                                {/* Filtro CSM (Apenas Gestores visualizando Toda a Carteira) */}
                                {view === PORTFOLIO_VIEWS.all && isManager && (
                                    <Col md={6}>
                                        <select
                                            className="form-select form-select-sm csm-select-input-sm"
                                            value={filterCsmId}
                                            onChange={e => applyFilterCsm(e.target.value)}
                                        >
                                            <option value="">Todos os CSMs / Sem Atribuição</option>
                                            {teamUsers
                                                .filter(u => u.platformRole === "cs" || u.platformRole === "admin" || u.platformRole === "owner")
                                                .map(u => (
                                                    <option key={u.uid || u.id} value={u.uid || u.id}>
                                                        {u.fullName || u.name}
                                                    </option>
                                                ))
                                            }
                                        </select>
                                    </Col>
                                )}
                            </Row>
                        </div>

                        {/* NeoTable */}
                        <NeoTable
                            columns={columns}
                            data={items}
                            loading={loading}
                            keyField="id"
                            onRowClick={(row) => setSelectedClient(row)}
                            emptyMessage="Nenhum cliente encontrado"
                            emptySubMessage="Ajuste os filtros de visualização ou a busca por nome."
                        />
                    </NeoCard>
                </Col>

                {/* Coluna Direita: Detalhes do Cliente Selecionado */}
                {activeClient && (
                    <Col lg={4} className="animate__animated animate__fadeInRight">
                        <NeoCard
                            title="Detalhes do Cliente"
                            headerIcon="mdi-information-outline"
                            thinBorder
                            bodyClassName="p-0"
                            className="h-100"
                            headerActions={
                                <NeoButton
                                    variant="soft-secondary"
                                    size="sm"
                                    square
                                    icon="mdi-close"
                                    onClick={() => setSelectedClient(null)}
                                    title="Fechar detalhes"
                                />
                            }
                        >
                            <div className="portfolio-detail-panel h-100">
                                <PlatformClientDetail
                                    client={activeClient}
                                    isManager={isManager}
                                    onTransfer={openTransfer}
                                />

                                {/* Atribuição Rápida se o cliente não tiver CSM */}
                                {isManager && !activeClient.csmId && (
                                    <div className="px-4 pb-4">
                                        <div className="csm-info-block-brand">
                                            <small className="text-brand-primary font-size-10 fw-bold text-uppercase d-block mb-2">
                                                Atribuição Rápida de CSM
                                            </small>
                                            <select
                                                className="form-select form-select-sm csm-select-input-sm"
                                                defaultValue=""
                                                onChange={async (e) => {
                                                    const val = e.target.value;
                                                    if (!val) return;
                                                    const u = teamUsers.find(user => (user.uid || user.id) === val);
                                                    if (u) {
                                                        await handleAssignCsm({
                                                            idTenant: activeClient.id,
                                                            csmUid: u.uid || u.id,
                                                            csmName: u.fullName || u.name
                                                        });
                                                    }
                                                }}
                                            >
                                                <option value="">Selecione um CS...</option>
                                                {teamUsers
                                                    .filter(u => u.platformRole === "cs" || u.platformRole === "admin" || u.platformRole === "owner")
                                                    .map(u => (
                                                        <option key={u.uid || u.id} value={u.uid || u.id}>
                                                            {u.fullName || u.name}
                                                        </option>
                                                    ))
                                                }
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </NeoCard>
                    </Col>
                )}
            </Row>

            {/* Modal de Transferência de CS (Individual ou Lote) */}
            <PlatformTransferModal
                isOpen={transferModal.open}
                toggle={closeTransfer}
                client={transferModal.client}
                clients={transferModal.clients}
                teamUsers={teamUsers}
                onConfirm={handleTransferCsm}
            />
        </div>
    );
};

export default PlatformPortfolio;
